import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, ArrowUp, Check, Loader2, RotateCw, SquarePen } from "lucide-react";
import { api } from "../api";
import { local } from "../storage";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import ChatMessage, { AnalystMark } from "../components/ChatMessage";
import PromptPicker, { type LibraryPrompt } from "../components/PromptPicker";
import { isAdsPrompt } from "../prompt-scope";
import { ErrorState, PageHeader, StatusBadge } from "../components/Ui";
import { AdsManagementStatus } from '../components/AdsManagement';
import { NoClient } from "../components/Workflow";
import { proposalState, readStream, STARTERS, type AiMessage, type AiProposal, type AiStreamEvent } from "../ads-ai";

/** The Ads AI: a chat that reads a client's Google Ads, LSA and Facebook ads and proposes changes a person applies. */
export default function AdsAi() {
  const { clientId, client } = useClientSelection();
  if (!clientId) return <><PageHeader title="Ads AI" description="Pick a client in the sidebar." /><NoClient /></>;
  return <Chat key={clientId} clientId={clientId} clientName={client?.name || "this client"} />;
}

type Session = { id: number; title: string | null; updated_at: string; running?: boolean };
// The conversation last open for each client, so leaving the page and coming back reopens it.
const lastKey = (clientId: number) => `nl-aos-ads-ai-session:${clientId}`;
const POLL_MS = 8000;
const ADS_PROMPTS = { label: "Google Ads, LSA and Facebook prompts", fits: isAdsPrompt };

function Chat({ clientId, clientName }: { clientId: number; clientName: string }) {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const sessionId = Number(params.get("session")) || null;
  const sessions = useApi<Session[]>(`/api/ads-ai/${clientId}/sessions`);
  // The same Prompt Library the AI Analyst uses, so the PPC and LSA prompts are one click away here too.
  const prompts = useApi<LibraryPrompt[]>("/api/prompts");
  const [prompt, setPrompt] = useState<LibraryPrompt | undefined>(undefined);
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(params.get("ask") || "");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<{ steps: string[]; proposals: AiProposal[] }>({ steps: [], proposals: [] });
  const [failure, setFailure] = useState<{ text: string; message: string } | null>(null);
  // An answer still being written on the server (the page was left mid-answer): shown and polled until saved.
  const [waiting, setWaiting] = useState(false);
  // Phones: the composer's hint is one short line; the client's full name wrapped to two.
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 520px)").matches);
  useEffect(() => {
    const mq = typeof window === "undefined" ? undefined : window.matchMedia?.("(max-width: 520px)");
    if (!mq) return;
    const update = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  const scroller = useRef<HTMLDivElement>(null);
  const followAnswer = useRef(true);
  const skipLoad = useRef(false);
  const alive = useRef(true);
  const loadingRequest = useRef<AbortController | null>(null);
  const streamRequest = useRef<AbortController | null>(null);
  const sending = useRef(false);
  const reopened = useRef(false);
  const poll = useRef<number | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; loadingRequest.current?.abort(); streamRequest.current?.abort(); if (poll.current) window.clearTimeout(poll.current); };
  }, []);

  // quiet: a poll for an answer still being written, so the thread stays on screen.
  const load = useCallback(async (id: number, quiet = false) => {
    loadingRequest.current?.abort();
    if (poll.current) { window.clearTimeout(poll.current); poll.current = null; }
    const controller = new AbortController(); loadingRequest.current = controller;
    if (!quiet) { setLoading(true); setLoadError(""); }
    try {
      const r = await api<{ session?: { running?: boolean }; messages: AiMessage[] }>(`/api/ads-ai/${clientId}/sessions/${id}`, { signal: controller.signal });
      if (!alive.current || controller.signal.aborted) return;
      setMessages(r.messages);
      const last = r.messages[r.messages.length - 1];
      const unanswered = last?.role === "user"; const running = Boolean(r.session?.running);
      setWaiting(unanswered && running);
      // The server stopped without saving an answer (it was cut off): offer the question again.
      const question = [...r.messages].reverse().find(m => m.role === 'user');
      setFailure(unanswered && !running ? { text: last.content, message: "This answer did not finish. Ask again to get it." }
        : last?.failed && question ? { text: question.content, message: last.content } : null);
      if (unanswered && running) poll.current = window.setTimeout(() => { if (alive.current) void load(id, true); }, POLL_MS);
      return r;
    } catch (e) {
      if (!alive.current || controller.signal.aborted) return;
      if (!quiet) setLoadError(e instanceof Error ? e.message : "The conversation could not load.");
      else poll.current = window.setTimeout(() => { if (alive.current) void load(id, true); }, POLL_MS);
    }
    finally { if (loadingRequest.current === controller) { loadingRequest.current = null; if (alive.current && !quiet) setLoading(false); } }
  }, [clientId]);
  useEffect(() => {
    followAnswer.current = true;
    if (sessionId) local.set(lastKey(clientId), String(sessionId));
    if (skipLoad.current) { skipLoad.current = false; return; }
    loadingRequest.current?.abort(); loadingRequest.current = null;
    if (poll.current) { window.clearTimeout(poll.current); poll.current = null; }
    streamRequest.current?.abort(); streamRequest.current = null; sending.current = false;
    setBusy(false); setWaiting(false); setLive({ steps: [], proposals: [] }); setFailure(null); setLoadError("");
    if (sessionId) void load(sessionId); else { setMessages([]); setLoading(false); }
  }, [sessionId, load, clientId]);
  // Coming back to the page reopens the conversation you had open (or one still being answered),
  // instead of a blank chat that made the old one look gone.
  useEffect(() => {
    if (reopened.current || !sessions.data) return;
    reopened.current = true;
    if (sessionId || params.get("ask")) return;
    const stored = Number(local.get(lastKey(clientId))) || null;
    if (stored && !sessions.data.some((x) => x.id === stored)) local.remove(lastKey(clientId)); // archived or not yours
    const pick = sessions.data.find((x) => x.id === stored) || sessions.data.find((x) => x.running);
    if (!pick) return;
    const next = new URLSearchParams(params); next.set("session", String(pick.id)); setParams(next, { replace: true });
  }, [sessions.data, sessionId, params, setParams, clientId]);
  useEffect(() => {
    const thread = scroller.current;
    if (!thread) return;
    // A short mobile viewport can make the welcome screen scroll. Keep its beginning visible.
    const welcome = !sessionId && !messages.length && !busy;
    if (welcome || followAnswer.current) thread.scrollTo({ top: welcome ? 0 : thread.scrollHeight, behavior: "auto" });
  }, [sessionId, messages.length, live.steps.length, live.proposals.length, busy]);

  async function send(text: string) {
    const t = text.trim();
    if (!t || sending.current || waiting || loadingRequest.current || loadError) return;
    sending.current = true;
    followAnswer.current = true;
    const controller = new AbortController(); streamRequest.current = controller;
    setBusy(true); setFailure(null); setDraft(""); setLive({ steps: [], proposals: [] });
    setMessages((list) => [...list, { id: `u${Date.now()}`, role: "user", content: t }]);
    let current = sessionId;
    try {
      await readStream(`/api/ads-ai/${clientId}/chat`, { session_id: current, message: t }, (ev: AiStreamEvent) => {
        if (!alive.current || controller.signal.aborted || streamRequest.current !== controller) return;
        if (ev.type === "session" && ev.id !== current) {
          current = ev.id;
          skipLoad.current = true; // this send's own events fill the thread
          const next = new URLSearchParams(params); next.set("session", String(ev.id)); next.delete("ask");
          setParams(next, { replace: true });
        }
        if (ev.type === "step") setLive((l) => ({ ...l, steps: [...l.steps, ev.label] }));
        if (ev.type === "proposal") setLive((l) => ({ ...l, proposals: [...l.proposals, ev.proposal] }));
        if (ev.type === "done") setMessages((list) => [...list, ev.message]);
        if (ev.type === "error") throw new Error(ev.error);
      }, controller.signal);
      if (alive.current && !controller.signal.aborted) sessions.reload();
    } catch (e) {
      if (alive.current && !controller.signal.aborted) {
        const failure = { text: t, message: e instanceof Error ? e.message : "The Ads AI could not answer." };
        if (current) {
          // A dropped stream does not mean the server stopped. Rejoin the saved
          // answer instead of resending the question into its still-running lock.
          setWaiting(true);
          const saved = await load(current, true);
          if (alive.current && !controller.signal.aborted && saved && saved.messages.at(-1)?.id === messages.at(-1)?.id) setFailure(failure);
          sessions.reload();
        } else setFailure(failure);
      }
    } finally {
      if (streamRequest.current === controller) {
        streamRequest.current = null; sending.current = false;
        if (alive.current) { setBusy(false); setLive({ steps: [], proposals: [] }); }
      }
    }
  }

  // A picked prompt rides ahead of what is typed (the typed text becomes the details it asks for),
  // like the AI Analyst. It applies to one message, then clears.
  function composeAndSend(typed: string) {
    const body = prompt?.prompt_body?.trim();
    const t = typed.trim();
    const composed = body ? (t ? `${body}\n\n${t}` : body) : t;
    if (!composed) return;
    if (composed.length > 4000) { setFailure({ text: composed, message: "This prompt and your message are over 4,000 characters together. Shorten the message." }); return; }
    void send(composed);
    if (prompt) setPrompt(undefined);
  }

  function newChat() {
    if (sending.current) return;
    local.remove(lastKey(clientId));
    loadingRequest.current?.abort(); loadingRequest.current = null; setLoading(false); setLoadError(""); setDraft("");
    const next = new URLSearchParams(params); next.delete("session"); next.delete("ask");
    setParams(next); setMessages([]); setFailure(null);
  }
  const updateProposal = (p: AiProposal) => setMessages((list) => list.map((m) => (m.proposals ? { ...m, proposals: m.proposals.map((x) => (x.id === p.id ? p : x)) } : m)));
  const lastAssistant = messages.map((m) => m.role).lastIndexOf("assistant");
  const empty = !sessionId && !messages.length && !busy;

  return (
    <div className="ads-ai-page">
      <PageHeader
        title="Ads AI"
        description={`Reads ${clientName}'s Google Ads, LSA calls and Facebook ads, and proposes changes. Nothing changes until someone approves it.`}
        actions={<>
          {(sessions.data?.length || 0) > 0 && (
            <select className="ads-ai-sessions" aria-label="Past conversations" disabled={busy} value={sessionId ?? ""} onChange={(e) => {
              const next = new URLSearchParams(params); if (e.target.value) next.set("session", e.target.value); else next.delete("session"); setParams(next);
            }}>
              <option value="">Past conversations</option>
              {sessions.data!.map((s) => <option key={s.id} value={s.id}>{s.title || `Conversation ${s.id}`}</option>)}
            </select>
          )}
          <button type="button" className="button secondary" onClick={newChat} disabled={busy}><SquarePen aria-hidden="true" />New chat</button>
        </>}
      />
      <AdsManagementStatus clientId={clientId} />
      {sessions.error && <p className="ads-ai-proposal-error" role="alert">Past conversations could not load. <button type="button" className="text-button" onClick={sessions.reload}>Reload conversations</button></p>}
      <div className="analyst-chat ads-ai-chat">
        <section className="ac-main" aria-label="Ads AI conversation">
          <div className="ac-scroll" ref={scroller} onScroll={e => {
            const el = e.currentTarget;
            followAnswer.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
          }}>
            {loading ? (
              <div className="ac-thread ac-thread-skeleton" aria-label="Loading conversation"><span className="ac-sk-user" /><span className="ac-sk-line" /><span className="ac-sk-line short" /></div>
            ) : loadError ? (
              <div className="ac-thread"><ErrorState message={loadError} onRetry={() => sessionId && void load(sessionId)} /></div>
            ) : empty ? (
              <div className="ac-welcome">
                <AnalystMark />
                <h2>Run {clientName}'s ads with the AI</h2>
                {/* One plain line: what it reads and who applies a change (no AI vendor names). */}
                <p>It reads the account, the client's notes and the SOP. {admin ? "Nothing changes until you press Apply." : "Nothing changes until a superadmin applies it."}</p>
                <div className="ac-starters">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" className="ac-starter" onClick={() => void send(s)}><span>{s}</span><ArrowRight aria-hidden="true" /></button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ac-thread" role="log" aria-label="Conversation with the Ads AI" aria-busy={busy}>
                {messages.map((m, i) => (
                  <ChatMessage key={m.id} message={{ id: m.id, role: m.role, content_text: m.content, created_at: m.created_at }} last={i === lastAssistant && !busy}>
                    {m.role === "assistant" && m.steps && m.steps.length > 0 && <Steps steps={m.steps} />}
                    {m.role === "assistant" && (m.proposals || []).map((p) => <ProposalCard key={p.id} proposal={p} admin={admin} userId={user?.id} onChange={updateProposal} />)}
                  </ChatMessage>
                ))}
                {waiting && !busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply">
                      <ul className="ads-ai-live"><li className="now"><Loader2 className="spin" aria-hidden="true" />Still working on this answer. It shows up here when it is done, even if you leave this page.</li></ul>
                    </div>
                  </div>
                )}
                {busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply">
                      <ul className="ads-ai-live">
                        {live.steps.map((s, i) => <li key={i} className={i === live.steps.length - 1 ? "now" : ""}>{i === live.steps.length - 1 ? <Loader2 className="spin" aria-hidden="true" /> : <Check aria-hidden="true" />}{s}</li>)}
                        {!live.steps.length && <li className="now"><Loader2 className="spin" aria-hidden="true" />Thinking</li>}
                      </ul>
                      <p className="muted ads-ai-leave-note">You can leave this page. The answer is saved in this conversation when it is done.</p>
                      {live.proposals.map((p) => <ProposalCard key={p.id} proposal={p} admin={admin} userId={user?.id} onChange={() => undefined} pendingRun />)}
                    </div>
                  </div>
                )}
                {failure && (
                  <div className="ac-error" role="alert">
                    <AlertCircle aria-hidden="true" />
                    <p><strong>No answer.</strong> {failure.message}</p>
                    <button type="button" className="ac-retry" onClick={() => void send(failure.text)}><RotateCw aria-hidden="true" />Retry</button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ac-composer-wrap">
            <form className="ac-composer" onSubmit={(e) => { e.preventDefault(); composeAndSend(draft); }}>
              <div className="ac-picker">
                <PromptPicker prompts={prompts} selected={prompt} onSelect={(p) => setPrompt(p || undefined)} disabled={busy || waiting || loading || !!loadError} scope={ADS_PROMPTS} />
              </div>
              <label htmlFor="ads-ai-message" className="sr-only">Message the Ads AI</label>
              <textarea id="ads-ai-message" className="ac-input" rows={1} value={draft} maxLength={4000} disabled={busy || waiting || loading || !!loadError}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); composeAndSend(draft); } }}
                placeholder={busy || waiting ? "The Ads AI is working…" : prompt ? "Add the details this prompt needs, or send it as is…" : narrow ? "Ask about the ads…" : `Ask about ${clientName}'s ads…`} />
              <button type="submit" className="ac-send" disabled={busy || waiting || loading || !!loadError || (!draft.trim() && !prompt)} aria-label="Send message"><ArrowUp aria-hidden="true" /></button>
            </form>
            <p className="ac-hint">Enter to send, Shift+Enter for a new line.</p>
          </div>
        </section>
      </div>
    </div>
  );
}

/** What the AI read for this answer, folded away. */
function Steps({ steps }: { steps: string[] }) {
  const review = steps.find(s => /^Review (complete|incomplete):/.test(s));
  const audit = steps.find(s => s.startsWith('Audit incomplete:'));
  return (
    <>
    {review && <p className={review.startsWith('Review incomplete:') ? 'ads-ai-proposal-error' : 'workflow-note'} role={review.startsWith('Review incomplete:') ? 'status' : undefined}>{review}</p>}
    {audit && <p className="workflow-note" role="status">{audit}</p>}
    <details className="ads-ai-steps">
      <summary>Sources and review steps</summary>
      <ul>{steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
    </details>
    </>
  );
}

/** One proposed change: Apply or Skip in place for whoever may approve it; otherwise where it waits. */
function ProposalCard({ proposal, admin, userId, onChange, pendingRun }: { proposal: AiProposal; admin: boolean; userId?: number; onChange: (p: AiProposal) => void; pendingRun?: boolean }) {
  const [busy, setBusy] = useState<"" | "apply" | "skip">("");
  const [note, setNote] = useState<{ tone: "danger" | "success"; text: string } | null>(null);
  const deciding = useRef(false);
  const state = proposalState(proposal);
  // The two-person rule: a superadmin may approve their own; anyone else's change waits for another person.
  const canDecide = state.key === "waiting" && (admin || (proposal.submitted_by !== userId && proposal.risk_level !== "HIGH"));
  async function decide(decision: "approved" | "rejected") {
    if (deciding.current || !canDecide || pendingRun) return;
    deciding.current = true;
    setBusy(decision === "approved" ? "apply" : "skip"); setNote(null);
    try {
      const r = await api<{ message: string; applied?: boolean }>(`/api/approvals/${proposal.id}`, { method: "PUT", body: JSON.stringify({ decision }) });
      if (r.applied === false) setNote({ tone: "danger", text: r.message });
      const fresh = await api<AiProposal[]>(`/api/ads-ai/proposals?ids=${proposal.id}`).catch(() => null);
      onChange(fresh?.[0] || { ...proposal, status: decision === "rejected" ? "rejected" : r.applied ? "executed" : "approved", ads_status: r.applied ? "applied" : proposal.ads_status });
    } catch (e) { setNote({ tone: "danger", text: e instanceof Error ? e.message : "That did not work." }); }
    finally { deciding.current = false; setBusy(""); }
  }
  return (
    <div className={`ads-ai-proposal is-${state.key}`}>
      <div className="ads-ai-proposal-main">
        <span className="ads-ai-proposal-kind">{state.label}</span>
        <strong>{proposal.title}</strong>
        {proposal.assessment && <details className="ads-ai-steps"><summary>Decision and evidence</summary>
          {Object.entries(proposal.assessment).map(([label, value]) => <p key={label}><strong>{label.replace(/_/g, " ")}:</strong> {value}</p>)}
        </details>}
        {proposal.is_ai_generated && !proposal.assessment && state.key === "waiting" && <p className="ads-ai-proposal-error">This older suggestion needs a fresh evidence review in Ads AI before it can be applied.</p>}
        {proposal.ads_error && state.key === "failed" && <p className="ads-ai-proposal-error">{proposal.ads_error}</p>}
        {note && <p className={`ads-ai-proposal-error ${note.tone}`}>{note.text}</p>}
      </div>
      <div className="ads-ai-proposal-actions">
        {pendingRun ? <StatusBadge>Proposed</StatusBadge>
          : canDecide ? (<>
            <button type="button" className="button" disabled={!!busy || (proposal.is_ai_generated === true && !proposal.assessment)} onClick={() => void decide("approved")}>{busy === "apply" ? "Applying…" : "Apply"}</button>
            <button type="button" className="text-button" disabled={!!busy} onClick={() => void decide("rejected")}>{busy === "skip" ? "Skipping…" : "Skip"}</button>
          </>)
            : <Link className="text-button" to={`/approvals?record=${proposal.id}`}>{state.key === "waiting" ? "In Approvals" : "Details"}</Link>}
      </div>
    </div>
  );
}
